<template>
  <div>
    <v-dialog
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="550"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          flat
          height="80"
          max-height="80"
        >
          <v-avatar :color="$t('Settings.Machine.EditDialog.Toolbar.Color')">
            <v-icon dark>
              {{ $t('Settings.Machine.EditDialog.Toolbar.Icon') }}
            </v-icon>
          </v-avatar>

          <v-toolbar-title class="pl-3">
            <div class="body-3">
              {{ $t('Settings.Machine.EditDialog.Toolbar.Title') }}
            </div>

            <div class="text-caption" />
          </v-toolbar-title>
          <v-spacer />
          <v-btn
            icon
            @click="onCancelButtonClick"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text class="pa-2">
          <v-form
            v-if="model"
            ref="editForm"
            v-model="formValid"
            @submit.prevent
          >
            <v-container
              fluid
            >
              <v-row dense>
                <v-col cols="12">
                  <v-text-field
                    v-model="model.hostname"
                    :hint="model.guid"
                    :label="$t('Settings.Machine.EditDialog.Form.Hostname.Label')"
                    disabled
                    persistent-hint
                    required
                  />
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="model.alias"
                    :hint="$t('Settings.Machine.EditDialog.Form.Alias.Hint')"
                    :label="$t('Settings.Machine.EditDialog.Form.Alias.Label')"
                    :maxlength="100"
                    :rules="validationRules.alias"
                    class="pr-2"
                    required
                  />
                </v-col>

                <v-col
                  v-if="action === 'printer'"
                  cols="12"
                >
                  <div class="d-flex align-center mt-3">
                    <v-icon
                      class="mr-2"
                      color="green"
                      small
                    >
                      mdi-printer-pos-network
                    </v-icon>
                    <div class="mr-3 mt-1 d-inline-flex">
                      {{ $t('Settings.Machine.EditDialog.Form.PrintRequired.Label') }}
                    </div>
                    <v-switch
                      v-model="model.print_required"
                      :label="model.print_required ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                      class="d-inline-flex ma-0"
                      color="success"
                      hide-details
                      inset
                    />
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions
          :class="[{'text-center': $vuetify.breakpoint.xsOnly}]"
          :style="$vuetify.breakpoint.xsOnly ? 'display: block;' : ''"
          class=""
        >
          <v-spacer class="hidden-xs-only" />

          <v-btn
            :disabled="isSaving"
            class="light-blue--text text--darken-1"
            outlined
            small
            @click="onCancelButtonClick"
          >
            {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
          </v-btn>

          <v-btn
            :disabled="!formValid || isSaving"
            :loading="isSaving"
            class="green white--text elevation-0"
            small
            @click="onSaveButtonClick"
          >
            {{ $t('Common.Button.Save').toLocaleUpperCase($i18n.locale) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>

import AppData from '@/mixins/appdata'
import DialogVisible from '@/mixins/dialogVisible.js'
import {clone} from '@/lib/helper/helper.js'

export default {
  name      : 'EditMachineDialog',
  components: {},
  directives: {},
  mixins    : [AppData, DialogVisible],
  props     : {
    selectedMachine: {
      type   : Object,
      default: undefined
    },
    action: {
      type     : String,
      default  : 'printer',
      validator: (v) => ['printer', 'display'].includes(v)
    }
  },
  data() {
    return {
      model          : null,
      formValid      : false,
      isSaving       : false,
      validationRules: {
        alias: [
          (v) => v && v.length >= 1 && v.length <= 100 || this.$t('Settings.Machine.EditDialog.Form.Alias.Error.Required')
        ]
      }
    }
  },
  computed: {},
  watch   : {
    isVisible(newVal) {
      if (newVal) {
        this.formValid = false
        this.isSaving = false
        this.model = clone(this.selectedMachine)
        this.$bus.$on(window.SocketCommand.System.Machines.UpdateDisplayMachine, this.onSaveResult)
        this.$bus.$on(window.SocketCommand.System.Machines.UpdatePrinterMachine, this.onSaveResult)
      }
    }
  },
  beforeCreate() {
  },
  created() {
  },
  beforeMount() {
  },
  mounted() {
  },
  beforeUpdate() {
  },
  updated() {
  },
  beforeDestroy() {
  },
  destroyed() {
  },
  methods: {
    onSaveButtonClick() {
      this.$refs.editForm.validate()

      if (this.formValid) {
        this.isSaving = true
        window.callAS(this.action === 'display' ? window.SocketCommand.System.Machines.UpdateDisplayMachine : window.SocketCommand.System.Machines.UpdatePrinterMachine, this.model)
      }
    },

    onSaveResult(data) {
      this.isSaving = false
      if (data.status === 'success') {
        this.onCancelButtonClick()
        this.$emit('save-success', clone(this.model))

        this.$bus.$emit('app-show-notification', {
          body: this.$t('Settings.Machine.EditDialog.Notification.Success'),
          type: 'success',
          icon: 'check'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Settings.Machine.EditDialog.Notification.Error'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    onCancelButtonClick() {
      this.closeDialog()
      this.model = null
      this.formValid = false
      this.isSaving = false
      this.$bus.$off(window.SocketCommand.System.Machines.UpdateDisplayMachine, this.onSaveResult)
      this.$bus.$off(window.SocketCommand.System.Machines.UpdatePrinterMachine, this.onSaveResult)
    }
  }
}
</script>

<style scoped>

</style>
